import React, { useState, useEffect } from 'react';
import { Activity, MessageSquare, Zap, Brain, Star, Wifi } from 'lucide-react';
import MessageViewer from '../components/MessageViewer';
import { messagesAPI } from '../services/api';
import axios from 'axios';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalAgents: 0,
    skillAgents: 0,
    personaAgents: 0,
    communicationAgents: 0,
    onlineAgents: 0,
    totalMessages: 0,
    avgLatency: 0
  });

  const [messageStats, setMessageStats] = useState(null);
  const [, setAgentStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch real stats from backend
    fetchStats();
    
    // Set up polling for real-time stats
    const interval = setInterval(fetchStats, 30000); // Update every 30 seconds
    
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAgentStats = async () => {
    try {
      // Get accurate stats from individual category API calls (like EnhancedAgents does)
      const timestamp = Date.now();
      const [allStats, skillStats, personaStats, commStats] = await Promise.all([
        axios.get(`/api/agents?type=all&limit=1&t=${timestamp}`),
        axios.get(`/api/agents?type=skill&limit=1&t=${timestamp}`),
        axios.get(`/api/agents?type=persona&limit=1&t=${timestamp}`), 
        axios.get(`/api/agents?type=communication&limit=1&t=${timestamp}`),
        axios.get(`/api/agents?type=mcp&limit=1&t=${timestamp}`)
      ]);
      
      // Get a sample of agents to calculate online percentage
      const sampleResponse = await axios.get(`/api/agents?type=all&limit=100&t=${timestamp}`);
      const sampleAgents = sampleResponse.data.agents || sampleResponse.data;
      const onlineAgents = sampleAgents.filter(agent => agent.status === 'online').length;
      const onlinePercentage = sampleAgents.length > 0 ? (onlineAgents / sampleAgents.length) : 0;
      const estimatedOnlineAgents = Math.round(allStats.data.pagination.total * onlinePercentage);

      const stats = {
        total: allStats.data.pagination.total,
        skill: skillStats.data.pagination.total,
        persona: personaStats.data.pagination.total,
        communication: commStats.data.pagination.total,
        online: estimatedOnlineAgents
      };

      setAgentStats(stats);
      
      console.log('Dashboard stats updated:', stats);
      return stats;
    } catch (error) {
      console.error('Failed to fetch agent stats:', error);
      return null;
    }
  };

  const fetchStats = async () => {
    try {
      // Fetch both agent and message stats
      const [agentData, messageData] = await Promise.all([
        fetchAgentStats(),
        messagesAPI.getStats().catch(() => null)
      ]);

      if (messageData) {
        setMessageStats(messageData);
      }

      // Update dashboard stats with real data
      if (agentData) {
        setStats({
          totalAgents: agentData.total,
          skillAgents: agentData.skill,
          personaAgents: agentData.persona,
          communicationAgents: agentData.communication,
          onlineAgents: agentData.online,
          totalMessages: messageData?.totalMessages || 0,
          avgLatency: 0 // Real telemetry data not available yet
        });
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type) => { // eslint-disable-line no-unused-vars
    switch (type) {
      case 'search': return <Activity size={16} />;
      case 'a2a': return <MessageSquare size={16} />;
      case 'response': return <Zap size={16} />;
      default: return <Activity size={16} />;
    }
  };

  const getActivityColor = (type) => { // eslint-disable-line no-unused-vars
    switch (type) {
      case 'search': return 'var(--accent)';
      case 'a2a': return 'var(--success)';
      case 'response': return 'var(--warning)';
      default: return 'var(--text-secondary)';
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-subtitle">NANDA Agent Network Overview</p>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px', marginBottom: '24px' }}>
        <div className="stat-card" style={{ padding: '24px 20px', textAlign: 'center' }}>
          <div className="stat-value" style={{ 
            fontSize: '2rem', 
            fontWeight: '300', 
            color: 'var(--text-primary)',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            marginBottom: '8px'
          }}>
            {loading ? 'Loading...' : stats.totalAgents.toLocaleString()}
          </div>
          <div className="stat-label" style={{ 
            fontSize: '0.75rem', 
            color: 'var(--text-secondary)',
            letterSpacing: '0.4px',
            textTransform: 'uppercase',
            fontWeight: '400'
          }}>Total Agents</div>
        </div>
        <div className="stat-card" style={{ padding: '24px 20px', textAlign: 'center' }}>
          <div className="stat-value" style={{ 
            fontSize: '2rem', 
            fontWeight: '300', 
            color: 'var(--text-primary)',
            fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            marginBottom: '8px'
          }}>
            {loading ? 'Loading...' : stats.totalMessages.toLocaleString()}
          </div>
          <div className="stat-label" style={{ 
            fontSize: '0.75rem', 
            color: 'var(--text-secondary)',
            letterSpacing: '0.4px',
            textTransform: 'uppercase',
            fontWeight: '400'
          }}>Messages Today</div>
        </div>
      </div>

      {/* Agent Type Breakdown */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header">
          <h3 className="card-title">Agent Distribution</h3>
          <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
            Real-time from MongoDB
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', background: 'var(--bg-tertiary)', borderRadius: '6px' }}>
            <Brain size={18} style={{ color: '#8b2020' }} />
            <div>
              <div style={{ fontSize: '1rem', fontWeight: '400', color: 'var(--text-primary)' }}>
                {loading ? 'Loading...' : stats.skillAgents.toLocaleString()}
              </div>
              <div style={{ fontSize: '0.6875rem', color: 'var(--text-secondary)' }}>
                Skill Agents
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', background: 'var(--bg-tertiary)', borderRadius: '6px' }}>
            <Star size={18} style={{ color: '#7b3030' }} />
            <div>
              <div style={{ fontSize: '1rem', fontWeight: '400', color: 'var(--text-primary)' }}>
                {loading ? 'Loading...' : stats.personaAgents.toLocaleString()}
              </div>
              <div style={{ fontSize: '0.6875rem', color: 'var(--text-secondary)' }}>
                Persona Agents
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', background: 'var(--bg-tertiary)', borderRadius: '6px' }}>
            <Wifi size={18} style={{ color: '#6b4040' }} />
            <div>
              <div style={{ fontSize: '1rem', fontWeight: '400', color: 'var(--text-primary)' }}>
                {loading ? 'Loading...' : stats.communicationAgents.toLocaleString()}
              </div>
              <div style={{ fontSize: '0.6875rem', color: 'var(--text-secondary)' }}>
                Communication Agents
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Real-time Messages */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Recent Messages</h3>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {messageStats && (
              <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                {messageStats.totalMessages} in last 24h
              </span>
            )}
            <div style={{ 
              width: '8px', 
              height: '8px', 
              backgroundColor: 'var(--success)', 
              borderRadius: '50%',
              animation: 'pulse 2s infinite'
            }}></div>
          </div>
        </div>
        <MessageViewer limit={10} />
      </div>

    </div>
  );
};

export default Dashboard;
